import React, { useEffect, useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Chart } from 'primereact/chart';
import { productionTimeAPI, ProductionTimeSummary } from '../services/api';
import { showCustomAlert } from './CustomAlert';

const ProductionTimeReports: React.FC = () => {
  const [dateRange, setDateRange] = useState<Date[]>(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return [start, end];
  });
  const [summary, setSummary] = useState<ProductionTimeSummary | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (dateRange[0] && dateRange[1]) {
      void loadSummary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange[0]?.toISOString(), dateRange[1]?.toISOString()]);

  const loadSummary = async () => {
    if (!dateRange[0] || !dateRange[1]) return;

    setLoading(true);
    try {
      const startDate = dateRange[0].toISOString().split('T')[0];
      const endDate = dateRange[1].toISOString().split('T')[0];

      const data = await productionTimeAPI.getEntries({
        periodStart: startDate,
        periodEnd: endDate
      });

      setSummary(data.summary as ProductionTimeSummary);
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('Error loading production time summary:', err);
      showCustomAlert.error('Greška pri učitavanju izveštaja');
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}min`;
  };

  const chartData = useMemo(() => {
    if (!summary) return null;

    const labels: string[] = [];
    const values: number[] = [];
    const colors: string[] = [];

    // Redovan rad
    if (summary.regularMinutes > 0) {
      labels.push('Redovan rad');
      values.push(summary.regularMinutes);
      colors.push('#2563eb');
    }

    // Specijalni modovi
    summary.deductionByState?.forEach((mode, index) => {
      labels.push(mode.stateName);
      values.push(mode.totalMinutes);
      // Koristi boju iz stanja ako postoji, inače koristi paletu
      colors.push(mode.stateColor || ['#a855f7', '#ef4444', '#f97316', '#22c55e', '#06b6d4'][index % 5]);
    });

    return {
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: colors,
          hoverBackgroundColor: colors
        }
      ]
    };
  }, [summary]);

  const chartOptions = {
    cutout: '70%',
    plugins: {
      legend: {
        position: 'bottom' as const
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const minutes = context.parsed;
            const hours = Math.floor(minutes / 60);
            const mins = minutes % 60;
            return `${context.label}: ${hours}h ${mins}min`;
          }
        }
      }
    }
  };

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ margin: 0, marginBottom: '1rem' }}>Izveštaji vremena proizvodnje</h1>

        <div
          style={{
            display: 'flex',
            gap: '1rem',
            alignItems: 'center',
            marginBottom: '1rem',
            flexWrap: 'wrap'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <label style={{ fontWeight: 'bold' }}>Period izveštaja:</label>
            <Calendar
              value={dateRange}
              onChange={(e) => setDateRange(e.value as Date[])}
              selectionMode="range"
              dateFormat="dd.mm.yy"
              showIcon
              readOnlyInput
            />
          </div>

          <Button
            label="Export PDF"
            icon="pi pi-file-pdf"
            onClick={() => showCustomAlert.info('Export PDF će biti implementiran uskoro')}
          />
        </div>
      </div>

      {summary && chartData && (
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1.5rem' }}>
          <Card>
            <h2 style={{ marginTop: 0 }}>Raspodela vremena po fazama</h2>
            <div style={{ maxHeight: '800px', display: 'flex', justifyContent: 'center' }}>
              <Chart type="doughnut" data={chartData} options={chartOptions} style={{ maxHeight: '800px', maxWidth: '800px' }} />
            </div>
            <div
              style={{
                textAlign: 'center',
                marginTop: '1.25rem'
              }}
            >
              <div style={{ fontSize: '0.9rem', color: '#6b7280' }}>Ukupno vreme</div>
              <div
                style={{
                  fontWeight: 'bold',
                  fontSize: '1.4rem',
                  marginTop: '0.15rem'
                }}
              >
                {formatDuration(summary.totalMinutes || 0)}
              </div>
            </div>
          </Card>

          <Card title="Detaljno po fazama">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <Card
                style={{
                  borderLeft: '4px solid #2563eb',
                  backgroundColor: '#eff6ff'
                }}
              >
                <div style={{ fontWeight: 'bold' }}>Redovan rad</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
                  {formatDuration(summary.regularMinutes || 0)}
                </div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                  Broj zapisa: 1
                </div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                  Procenat:{' '}
                  {(
                    ((summary.regularMinutes || 0) / (summary.totalMinutes || 1)) *
                    100
                  ).toFixed(1)}
                  %
                </div>
              </Card>

              {summary.deductionByState?.map((mode, index) => (
                <Card
                  key={mode.stateId}
                  style={{
                    borderLeft: `4px solid ${mode.stateColor || '#f97316'}`,
                    backgroundColor: index % 2 === 0 ? '#fff7ed' : '#fef2ff'
                  }}
                >
                  <div style={{ fontWeight: 'bold' }}>{mode.stateName}</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
                    {formatDuration(mode.totalMinutes)}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                    Broj zapisa: {mode.entryCount}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                    Procenat:{' '}
                    {((mode.totalMinutes / (summary.totalMinutes || 1)) * 100).toFixed(1)}%
                  </div>
                </Card>
              ))}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ProductionTimeReports;